import dayjs from "dayjs";

import { diDecorator } from "@/backend/di/diDecorator";
import { EPaymentPlan } from "@/backend/domains/subscription/data/paymentPlans";
import { RepositoryOptions } from "@/backend/shared/repository/base.repository";
import { ExtractEntity } from "@/backend/shared/repository/BaseEntity";
import { Filter, Update } from "@/backend/shared/repository/types";

import { OmitByKey } from "@/types/customTypes";
import { appErrors } from "@/errors";
import { doIt } from "@/utils/doIt";
import { isPassed } from "@/utils/isPassed";
import obj from "@/utils/obj";

import { BaseUpdatableService } from "../../../shared/application/base.service";
import { type InvoiceRepository } from "../../invoice/domain/invoice.repository";
import { type TiptopApi } from "../../payment-system/infrastructure/tiptop";
import {
  Actions,
  PERMISSION_ACTIONS,
  PermissionActions,
  Resourses,
} from "../../permission/domain/Permission";
import { FEATURES, Features } from "../domain/PaymentPlan";
import { type PaymentPlanRepository } from "../domain/paymentPlan.repository";
import {
  ESubscriptionStatus,
  ISubscription,
  ISubscriptionCreating,
  SubscriptionStatus,
  subscriptionStatusesMap,
} from "../domain/Subscription";
import { type SubscriptionRepository } from "../domain/subscription.repository";
import { SubscriptionHistoryService } from "./subscriptionHistory.service";

//

export type PaymentPlanUpdateData = OmitByKey<
  ExtractEntity<ISubscriptionCreating>,
  "organizationId" | "activatedAt"
>;

//

@diDecorator.injectable()
export class SubscriptionService extends BaseUpdatableService<
  ISubscription,
  ISubscriptionCreating
> {
  constructor(
    @diDecorator.inject("SubscriptionRepository")
    public repository: SubscriptionRepository,
    @diDecorator.inject("PaymentPlanRepository")
    private paymentPlanRepository: PaymentPlanRepository,
    @diDecorator.inject("SubscriptionHistoryService")
    private subscriptionHistoryService: SubscriptionHistoryService,
    @diDecorator.inject("InvoiceRepository")
    private invoiceRepository: InvoiceRepository,
    @diDecorator.inject("PaymentSystem")
    private paymentSystem: TiptopApi,
  ) {
    super(repository);
  }

  //

  public async getByOrganizationId(organizationId: string) {
    return await this.repository.requireOne({ organizationId });
  }

  //

  public async findBySubscriptionId(subscriptionId: string) {
    return await this.repository.findOne({ paymentSubscriptionId: subscriptionId });
  }

  //

  public async requireBySubscriptionId(subscriptionId: string) {
    return await this.repository.requireOne({ paymentSubscriptionId: subscriptionId });
  }

  //

  public async findActive(
    filter: Filter<ISubscription> = {},
    options?: RepositoryOptions<ISubscription>,
  ) {
    return await this.repository.find(
      {
        $or: [{ status: ESubscriptionStatus.ACTIVE }, { expiresAt: { $gt: new Date() } }],
        ...filter,
      },
      options,
    );
  }

  //

  public async handlePaymentPlanUpdate(
    organizationId: string,
    data: PaymentPlanUpdateData,
    session?: unknown,
  ): Promise<ISubscription> {
    const subscription = await doIt(async () => {
      const existed = await this.repository.findOne({ organizationId });

      if (!existed) {
        return this.create({ ...data, organizationId }, session);
      }

      const isNewPlan = data.planId !== existed.planId;
      const toUpdate: Update<ISubscription> = { ...data, $unset: { nextPlan: "" } };

      if (isNewPlan) {
        if (
          existed.paymentSubscriptionId &&
          data.paymentSubscriptionId !== existed.paymentSubscriptionId
        ) {
          await this.paymentSystem.cancelSubscription(existed.paymentSubscriptionId);
        }
        return await this.updatePaymentPlan(organizationId, toUpdate, session);
      } else {
        return await this.updateStatus(organizationId, toUpdate, session);
      }
    });

    return subscription;
  }

  //

  public async activateFree(organizationId: string, session?: unknown) {
    const invoice = await this.invoiceRepository.create(
      {
        organizationId,
        amount: 0,
        description: "Активация бесплатного тарифа",
        data: {
          type: "subscription",
          amount: 0,
          interval: "Month",
          paymentPlan: EPaymentPlan.FREE,
          paymentPlanMonthlyPrice: 0,
          period: 1,
          authorizingPayment: false,
          orgPromocodeId: null,
        },
      },
      session,
    );
    return this.handlePaymentPlanUpdate(
      organizationId,
      { invoiceId: invoice.id, planId: EPaymentPlan.FREE, status: ESubscriptionStatus.ACTIVE },
      session,
    );
  }

  //

  public async canTrial(organizationId: string) {
    const subscription = await this.repository.findOne({ organizationId });

    if (!subscription) return true;
    if (subscription.planId !== EPaymentPlan.FREE) return false;
    if (subscription.status === ESubscriptionStatus.TRIAL) return false;

    const hasTrial = await this.subscriptionHistoryService.repository.exists({
      organizationId,
      "data.status": ESubscriptionStatus.TRIAL,
    });

    if (hasTrial) return false;

    return true;
  }

  //

  public async activateTrial(
    organizationId: string,
    options?: { paymentPlan?: EPaymentPlan; trialDays?: number; session?: unknown },
  ) {
    const { paymentPlan = EPaymentPlan.PRO_PLUS, trialDays = 3, session } = options ?? {};
    const expiresAt = dayjs().add(trialDays, "days").toDate();

    const invoice = await this.invoiceRepository.create(
      {
        organizationId,
        amount: 0,
        description: "Активация пробного периода",
        data: {
          type: "subscription",
          amount: 0,
          interval: "Month",
          paymentPlan,
          paymentPlanMonthlyPrice: 0,
          period: 1,
          authorizingPayment: false,
          orgPromocodeId: null,
        },
      },
      session,
    );

    return this.handlePaymentPlanUpdate(
      organizationId,
      {
        invoiceId: invoice.id,
        planId: paymentPlan,
        status: ESubscriptionStatus.TRIAL,
        expiresAt,
      },
      session,
    );
  }

  //

  private async create(data: OmitByKey<ISubscriptionCreating, "activatedAt">, session?: unknown) {
    const activatedAt = new Date();
    const created = await this.repository.create({ ...data, activatedAt }, session);
    await this.subscriptionHistoryService.addCreated(created);
    return created;
  }

  //

  public async updatePaymentPlan(
    organizationId: string,
    data: Update<
      Partial<OmitByKey<ISubscription, "id" | "organizationId" | "updatedAt" | "createdAt">>
    >,
    session?: unknown,
  ) {
    const toUpdate: Update<ISubscription> = { ...data, activatedAt: new Date() };
    const updated = await this.repository.requireOneAndUpdate({ organizationId }, toUpdate, {
      session,
    });
    await this.subscriptionHistoryService.addPlanUpdated(updated, session);
    return updated;
  }

  //

  public async setNextPaymentPlan(
    organizationId: string,
    data: ISubscription["nextPlan"] extends infer T | null ? T : never,
    session?: unknown,
  ) {
    return this.repository.startSession(async (_session) => {
      const s = session ?? _session;

      const toUpdate: Update<ISubscription> = { nextPlan: data };
      const updated = await this.updateStatus(organizationId, toUpdate, s);

      const invoiceId = data.invoiceId;
      const invoice = await this.invoiceRepository.requireSubscriptionInvoice({ id: invoiceId });

      if (!updated.paymentSubscriptionId) {
        throw new appErrors.AppError(
          `Ошибка при обновлении подписки: paymentSubscriptionId is undefined`,
        );
      }

      await this.paymentSystem.updateSubscriptionByInvoice(updated.paymentSubscriptionId, invoice);

      return updated;
    });
  }

  //

  public async unsetNextPaymentPlan(organizationId: string, session?: unknown) {
    return this.repository.startSession(async (_session) => {
      const s = session ?? _session;

      const toUpdate: Update<ISubscription> = { $unset: { nextPlan: "" } };
      const updated = await this.updateStatus(organizationId, toUpdate, s);

      const invoiceId = updated.invoiceId;
      const invoice = await this.invoiceRepository.requireSubscriptionInvoice({ id: invoiceId });

      if (!updated.paymentSubscriptionId) {
        throw new appErrors.AppError(
          `Ошибка при обновлении подписки: paymentSubscriptionId is undefined`,
        );
      }

      await this.paymentSystem.updateSubscriptionByInvoice(updated.paymentSubscriptionId, invoice);

      return updated;
    });
  }

  //

  public async cancelSubscription(organizationId: string) {
    const subscription = await this.getByOrganizationId(organizationId);
    if (!subscription.paymentSubscriptionId) {
      throw new appErrors.NotFoundError("Подписка не содержит paymentSubscriptionId");
    }
    const { paymentSubscriptionId, invoiceId, planId } = subscription;
    await this.paymentSystem.cancelSubscription(paymentSubscriptionId);
    const { Model: updated } = await this.paymentSystem.getSubscription(paymentSubscriptionId);

    if (updated.Status !== "Cancelled") {
      throw new appErrors.AppError("Subscription status is not cancelled");
    }

    const data: PaymentPlanUpdateData = {
      invoiceId,
      planId,
      paymentSubscriptionId,
      status: ESubscriptionStatus.CANCELED,
      nextBillingAt: null,
    };

    return await this.handlePaymentPlanUpdate(organizationId, data);
  }

  //

  public async enableAutoRenewalSubscription(organizationId: string, invoiceId: string) {
    const subscription = await this.getByOrganizationId(organizationId);
    if (!subscription.paymentSubscriptionId) {
      throw new appErrors.NotFoundError("Подписка не содержит paymentSubscriptionId");
    }
    const { paymentSubscriptionId, planId } = subscription;
    const invoice = await this.invoiceRepository.requireSubscriptionInvoice({ id: invoiceId });

    await this.paymentSystem.updateSubscriptionByInvoice(paymentSubscriptionId, invoice);

    const { Model: updated } = await this.paymentSystem.getSubscription(paymentSubscriptionId);

    if (updated.Status !== "Active") {
      throw new appErrors.AppError("Subscription status is not activated");
    }

    const data: PaymentPlanUpdateData = {
      invoiceId,
      planId,
      paymentSubscriptionId,
      status: ESubscriptionStatus.ACTIVE,
    };

    if (updated.NextTransactionDateIso) {
      data.nextBillingAt = new Date(updated.NextTransactionDateIso);
    }

    return await this.handlePaymentPlanUpdate(organizationId, data);
  }

  //

  public async updateStatus(
    organizationId: string,
    data: Update<
      Partial<
        OmitByKey<
          ISubscription,
          "id" | "organizationId" | "updatedAt" | "createdAt" | "planId" | "paymentSubscriptionId"
        >
      >
    >,
    session?: unknown,
  ) {
    const updated = await this.repository.requireOneAndUpdate({ organizationId }, data, {
      session,
    });
    await this.subscriptionHistoryService.addStatusUpdated(updated, session);
    return updated;
  }

  //

  public async checkStatus(subscriptionId: string): Promise<SubscriptionStatus> {
    const { status, expiresAt, pausedUntil } = await this.repository.requireById(subscriptionId);
    const { access: _access, ...other } = subscriptionStatusesMap[status];

    const access = doIt(() => {
      if (pausedUntil && !isPassed(pausedUntil)) return false;
      if (expiresAt && isPassed(expiresAt)) return false;
      if (expiresAt && !isPassed(expiresAt)) return true;
      return _access;
    });

    return { access, ...other };
  }

  //

  public async hasFeature(subscriptionId: string, feature: keyof Features): Promise<boolean> {
    const plan = await this.repository.getPlanById(subscriptionId);
    const features = await this.paymentPlanRepository.getFeatures(plan);
    return features[feature];
  }

  //

  public async canDoAction(subscriptionId: string, permissionAction: PermissionActions) {
    const actions = await this.checkActions(subscriptionId);
    return actions[permissionAction];
  }

  //

  public async checkActions(subscriptionId: string): Promise<Record<PermissionActions, boolean>> {
    const plan = await this.repository.getPlanById(subscriptionId);
    const features = await this.paymentPlanRepository.getFeatures(plan);

    const result: Partial<Record<PermissionActions, boolean>> = {};

    PERMISSION_ACTIONS.forEach((permissionAction) => {
      const [resourse, action] = permissionAction.split(".") as [Resourses, Actions];
      const permission = obj.entries(features).some(({ key: feature, value }) => {
        if (!value) return false;
        const { permissions } = FEATURES[feature];
        const actions = permissions[resourse] ?? [];
        return actions.some((a) => a === action);
      });
      result[permissionAction] = permission;
    });

    return result as Record<PermissionActions, boolean>;
  }

  // STATIC

  public static checkStatus(subscription: ISubscription) {
    const { status, expiresAt, pausedUntil } = subscription;
    const { access: _access, ...other } = subscriptionStatusesMap[status];

    const access = doIt(() => {
      if (pausedUntil && !isPassed(pausedUntil)) return false;
      if (expiresAt && isPassed(expiresAt)) return false;
      if (expiresAt && !isPassed(expiresAt)) return true;
      return _access;
    });

    return {
      ...other,
      access,
      status: (access ? ESubscriptionStatus.ACTIVE : status) satisfies ESubscriptionStatus,
    };
  }
}
